import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { BiGrantView, BiMetadataView } from '../types'

const BASE = '/v1/bi'

export function useBiMetadata() {
  return useQuery({
    queryKey: ['bi', 'metadata'],
    queryFn: async () => (await apiClient.get<BiMetadataView>(`${BASE}/metadata`)).data,
    // Read from the live catalogue, so it changes only when a migration adds a view.
    staleTime: 5 * 60_000,
  })
}

export function useBiGrants() {
  return useQuery({
    queryKey: ['bi', 'grants'],
    queryFn: async () => (await apiClient.get<BiGrantView[]>(`${BASE}/grants`)).data,
  })
}

export function useBiGrantMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bi', 'grants'] })

  return {
    grant: useMutation({
      mutationFn: async (body: {
        dbRoleName: string
        legalEntityId: string | null
        description: string | null
      }) => (await apiClient.post<BiGrantView>(`${BASE}/grants`, body)).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/grants/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}
